import { FormArray, FormControl, FormGroup } from '@angular/forms';

import type { RepeatableGroupConfiguration } from '../../../interfaces/input.interface';
import type { RepeatableLogger } from './repeatable-group.types';
import { RepeatableValueUtils } from './repeatable-value.utils';

export type RepeatableModelSerializerOptions = {
    source: string;
    model: FormGroup | null;
    rowsArray: FormArray<FormGroup>;
    configuration: RepeatableGroupConfiguration;
    isEditable: boolean;
    isHydratingFromModel: boolean;
    hasPendingDropdowns: boolean;
    suspendSyncToModel: boolean;
    skipNextModelHydrationControls: Set<string>;
};

export class RepeatableModelSerializer {
    public constructor(private readonly logger: RepeatableLogger) {}

    public syncRowsToModel(options: RepeatableModelSerializerOptions): boolean {
        if (options.isHydratingFromModel) {
            this.logger.warn('syncRowsToModel(): skipped during hydrateFromModel().', {
                source: options.source,
            });
            return false;
        }

        if (options.hasPendingDropdowns) {
            this.logger.warn('syncRowsToModel(): skipped because dropdown resolution is still pending.', {
                source: options.source,
            });
            return false;
        }

        if (!options.model) {
            this.logger.warn('syncRowsToModel(): model missing, skipping.', { source: options.source });
            return false;
        }

        if (options.suspendSyncToModel) {
            this.logger.warn('syncRowsToModel(): skipped (model not ready).', { source: options.source });
            return false;
        }

        const separator = options.configuration.serialization?.separator ?? ';';
        const rows = RepeatableValueUtils.getRowsSnapshot(options.rowsArray);
        const subFields = options.configuration.subFields ?? [];

        for (const [logicalKey, targetControlName] of Object.entries(options.configuration.bindings ?? {})) {
            const subField = subFields.find((item) => item.key === logicalKey);
            const joinedValue = rows
                .map((row) => RepeatableValueUtils.serializeCellValue(row[logicalKey], subField, !options.isEditable))
                .join(separator);
            const control = options.model.get(targetControlName);

            if (control) {
                const previousValue = String(control.value ?? '');

                if (previousValue !== joinedValue) {
                    options.skipNextModelHydrationControls.add(targetControlName);
                    control.patchValue(joinedValue, { emitEvent: true });

                    if (options.isEditable) {
                        control.markAsDirty({ onlySelf: true });
                        control.markAsTouched({ onlySelf: true });
                    }

                    this.logger.log('syncRowsToModel: patched existing model control.', {
                        source: options.source,
                        logicalKey,
                        targetControlName,
                        previousValue,
                        joinedValue,
                    });
                }
            } else {
                options.model.addControl(targetControlName, new FormControl(joinedValue));
                options.model.updateValueAndValidity({ emitEvent: false });

                this.logger.warn('syncRowsToModel: model control missing; created it.', {
                    source: options.source,
                    logicalKey,
                    targetControlName,
                    joinedValue,
                });
            }
        }

        return true;
    }

    public isAnyBindingNonEmpty(model: FormGroup | null, entries: Array<[string, string]>): boolean {
        if (!model) {
            return false;
        }

        return entries.some(([, controlName]) => RepeatableValueUtils.hasNonEmptyValue(model.get(controlName)?.value));
    }
}
